import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { ISegment, IProfileRecord } from "../../../../Models/Models";
import { FiExternalLink } from "@react-icons/all-files/fi/FiExternalLink";
import { FiCheck } from "@react-icons/all-files/fi/FiCheck";
import { GiAchillesHeel } from "@react-icons/all-files/gi/GiAchillesHeel";
import {
    isOutputSelected,
    registerElement,
    registerRef,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { RenderType } from "../../../../Enums/RenderType";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";

import { PolygonNameWithCircle, PsychProfSumm } from "../../../Common/";

import styled from "styled-components";
import { Modal, Radio } from "antd";

import {
    ReportCheckbox,
    ReportH4,
    ChevronDown,
    ColSegInfoWrapper,
    ColSegInfo,
    Menu,
    MenuLink,
    SegCell,
    SortSegRow,
    SegCirc,
    SortSegBody,
    SortSegFooter,
    SortSegHead,
    SortSegHeadActions,
    SegImg,
    SegDetails,
    SegLink,
    useWindowResize,
    SegCircTextL,
    SegCircTextSm,
    SegCellTextL,
    SegCellTextSm,
    SegCellTextSmB,
    getPrettyNumber,
    SegContentSecHeader,
    SegFooterSubText,
    SortSegCardBreakoutContainer,
    SegCardBreakoutItem,
    SegCardBreakoutVerticalBreak,
    findSegIcon,
    SegCardRow,
    SegCardCol,
    SegCardSpacer,
    SegCardHead,
    SegCardHeaderRow,
    determinePropensityText,
    SortSegCellWrapper,
    BuxTooltip,
    AscIcon,
    DescIcon,
    EmptyResultsCell,
} from "../../../Common";
import { deviceWidth } from "../../../../globalStyles";
import { SortSegCellWrapperWithActivePolygon } from "./HHCharacteristicsStyles";
import GPSPsychProfileShare from "./GPSPsychProfileShare";
import { gpsMosaicSegmentsSelector } from "../../../../Reducers/Selectors/GPSInsightsSelectors";

interface IdealVisitorSegmentsProps {
    location1Name: string;
    location2Name?: string;
    storeProfileData: IProfileRecord[];
    benchmarkProfileData?: IProfileRecord[];
    renderType: RenderType;
    app?: DigitalShareAppType;
    itemType?: DigitalShareItemType;
}
interface IProfCardContent {
    segment: ISegment;
    seg1: IProfileRecord;
    seg2?: IProfileRecord;
}
interface ISortOption {
    dir: "asc" | "desc";
    type: "%" | "prop" | null;
}

const StyledCheck = styled(FiCheck)`
  width: 17px;
  height: 17px;
  margin-right: 2px;
  flex-shrink: 0;
`;

const PctNumWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  height: 30px;
`;

export const IdealVisitorSegments: FunctionComponent<IdealVisitorSegmentsProps>
    = (props: IdealVisitorSegmentsProps) => {
        const {
            location1Name,
            location2Name,
            storeProfileData,
            benchmarkProfileData,
        } = props;
        const mosaicSegments = useSelector(gpsMosaicSegmentsSelector);

        const colors = { P: "#fd504d", S: "#5e73ff", N: "#c4c4c4", T: "#c4c4c4" };
        const propensityToolTipText = `Visitor propensity compares the distribution of segments observed at the selection to the distribution of segments across all households in the selection’s trade area. The ratio between these percentages for each segment defines the propensity for households of that segment to be a visitor to the selected place (as compared to all other households). For example, a propensity of 2.5 indicates the segment is 2.5 times more likely than the average household in the trade area to visit the selection.  PRIMARY AND SECONDARY VISITOR HOUSEHOLDS HAVE A PROPENSITY OF GREATER THAN 1.0x AND REPRESENT MORE THAN 1% OF VISITORS.`;

        const [filterOpen, setIsFilterMenuOpen] = useState<boolean>(false);
        const [sort1Open, setIsSort1MenuOpen] = useState<boolean>(false);
        const [sort2Open, setIsSort2MenuOpen] = useState<boolean>(false);
        const [filters, setFilters] = useState<string[]>(["P", "S"]);
        const [profData, setProfData] = useState<IProfileRecord[]>(null);
        const [visibleModal, setVisibleModal] = useState<boolean>(false);
        const [modalContent, setModalContent] = useState<IProfCardContent>(null);
        const [sortCol, setSortCol] = useState<1 | 2>(1);
        const [sort1Option, setSort1Option] = useState<ISortOption>({
            dir: "desc",
            type: "prop",
        });
        const [sort2Option, setSort2Option] = useState<ISortOption>({
            dir: "desc",
            type: "prop",
        });

        const windowSize = useWindowResize();

        useEffect(() => {
            if (!profData) {
                filterData();
            }
        });

        useEffect(() => {
            if (profData) {
                filterData();
            }
        }, [filters]);

        useEffect(() => {
            if (modalContent) {
                setVisibleModal(true);
            }
        }, [modalContent]);

        useEffect(() => {
            if (profData) {
                setProfData(
                    sortData(profData, sortCol === 1 ? sort1Option : sort2Option)
                );
            }
        }, [sort1Option, sort2Option, sortCol]);

        const filterData = () => {
            let tempProfData = [...storeProfileData];
            if (filters.length > 0) {
                tempProfData = storeProfileData.filter((storeProf) => {
                    let benchmarkCheck = false;
                    if (benchmarkProfileData?.length > 0) {
                        const benchmarkProfile = benchmarkProfileData.find(
                            (benProfDat) => benProfDat.seg === storeProf.seg
                        );
                        benchmarkCheck = benchmarkProfileData
                            ? filters.includes(benchmarkProfile.core)
                            : false;
                    } else {
                        benchmarkCheck = false;
                    }

                    return filters.includes(storeProf.core) || benchmarkCheck;
                });
            }

            setProfData(
                sortData(tempProfData, sortCol === 1 ? sort1Option : sort2Option)
            );
        };

        const sortData = (
            profileData: IProfileRecord[],
            sortOption: ISortOption
        ) => {
            const sortArr = sortCol === 1 ? profileData : benchmarkProfileData; // ERY: depending on the column, I'm sorting different data
            const tempProfData = profileData; // ERY: but the profileData is what needs to be sorted in the output.
            switch (sortOption.type) {
                case "prop": {
                    sortArr.sort((a: IProfileRecord, b: IProfileRecord) => {
                        return a.index - b.index;
                    });
                    break;
                }
                case "%": {
                    sortArr.sort((a: IProfileRecord, b: IProfileRecord) => {
                        return a.pct - b.pct;
                    });
                    break;
                }
                default: {
                    sortArr.sort(sortText);
                    break;
                }
            }

            // ERY: so, sort the tempProfData based on which ever data is selected and output that. Noodly, I know. At least we don't build code for Toyota. :-P
            tempProfData.sort((a, b) => {
                const profDataA = sortArr.find((el) => el.seg === a.seg);
                const profDataB = sortArr.find((el) => el.seg === b.seg);
                return sortArr.indexOf(profDataA) - sortArr.indexOf(profDataB);
            });
            return sortOption.dir === "asc"
                ? [...tempProfData]
                : [...tempProfData.reverse()];
        };

        const sortText = (a: IProfileRecord, b: IProfileRecord): number => {
            const segA = a.seg.toLowerCase();
            const segB = b.seg.toLowerCase();

            if (segA < segB) return -1;
            if (segA > segB) return 1;
            return 0;
        };

        const onSortColChange = (e) => {
            setSortCol(e.target.value);
        };

        const formatFilterText = (): string => {
            let fmtStr = "All";
            if (filters.length > 1) {
                fmtStr = "Primary/Secondary";
            } else if (filters.includes("P")) {
                fmtStr = "Primary";
            } else if (filters.includes("S")) {
                fmtStr = "Secondary";
            }
            return fmtStr;
        };

        const formatSortingText = (sortObj: ISortOption): string => {
            let text = "Segment Name";
            switch (sortObj.type) {
                case "prop":
                    text = "Visitor Propensity";
                    break;
                case "%":
                    text = "% of Visitors";
                    break;
                default:
                    break;
            }
            return text;
        };

        const handleMenuClick = (filterVal: "P" | "S") => {
            setIsFilterMenuOpen(false);

            if (filterVal) {
                // Toggle filter off if already selected
                if (filters.includes(filterVal)) {
                    const ind = filters.indexOf(filterVal);
                    filters.splice(ind, 1);
                } else {
                    filters.push(filterVal);
                }
                setFilters([...filters]);
            } else {
                setFilters([]);
            }
        };

        const handleSortOptionClick = (sortCol: number, sortObj: ISortOption) => {
            if (sortCol === 2) {
                setSort2Option(sortObj);
            } else {
                setSort1Option(sortObj);
            }
        };

        const calcPctHH = (pct: number): string => {
            return getPrettyNumber(pct * 100, 2);
        };

        const psychProfileRef = useRef();
        const psychSummaryRef = useRef();

        if (
            featureFlags.DigitalShare
            && props.renderType === RenderType.DIGITAL_SHARE
            && isOutputSelected(props.app, DigitalShareItemType.PsychographicProfile)
        ) {
            registerRef(
                props.app,
                DigitalShareItemType.PsychographicProfile,
                psychProfileRef
            );

            registerElement(
                props.app,
                DigitalShareItemType.PsychographicProfile,
                "",
                <GPSPsychProfileShare
                    segments={mosaicSegments}
                    topSegs={profData.slice(0, 5)}
                    polygons={[location1Name, location2Name]}
                    benchmarkProfileData={benchmarkProfileData}
                />
            );
        }

        if (
            featureFlags.DigitalShare
            && props.renderType === RenderType.DIGITAL_SHARE
            && isOutputSelected(props.app, DigitalShareItemType.PsychographicSummary)
        ) {
            const topSegs = profData.slice(0, 5); // take top 5 (can be less than 5)
            registerRef(
                props.app,
                DigitalShareItemType.PsychographicSummary,
                psychSummaryRef
            );

            registerElement(
                props.app,
                DigitalShareItemType.PsychographicSummary,
                "",
                <PsychProfSumm segDets={mosaicSegments} topSegs={topSegs} />
            );
        }

        return (
            <>
                <>
                    <ReportH4 style={{ lineHeight: "normal" }}>
                        Ideal visitor segments are based on lifestyle, life stage,
                        behavioral, and consumer characteristics, based on mobile data
                        observations of actual customers at the selected location(s).
                    </ReportH4>
                    <ColSegInfoWrapper>
                        <ColSegInfo
                            dotColor={colors.P}
                            title={<>Primary Visitor</>}
                            text={`Households 20+% more likely to visit than the average household`}
                        />
                        <ColSegInfo
                            dotColor={colors.S}
                            title={<>Secondary Visitor</>}
                            text={`Households up to 19% more likely to visit than the average household`}
                        />
                        <ColSegInfo
                            dotColor={colors.N}
                            title={<>Incidental Visitor</>}
                            text={`Households less likely to visit than the average household`}
                        />
                    </ColSegInfoWrapper>
                    <SortSegHead>
                        <SortSegRow style={{ border: "0px" }}>
                            <SortSegCellWrapper>
                                <SegCell>
                                    {windowSize.width < deviceWidth.desktop ? (
                                        <Menu
                                            title={formatFilterText()}
                                            type="filled"
                                            bgColor="black"
                                            color="white"
                                            isOpen={filterOpen}
                                            setIsMenuOpen={setIsFilterMenuOpen}
                                            style={{ margin: 0 }}
                                            fontSize={11}
                                            align="left"
                                        >
                                            <MenuLink
                                                title={
                                                    <>
                                                        {filters.includes("P") ? <StyledCheck /> : null}
                                                        Primary
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleMenuClick("P");
                                                }}
                                                className={filters.includes("P") ? "active" : null}
                                                style={{ fontSize: "12px" }}
                                            />
                                            <MenuLink
                                                title={
                                                    <>
                                                        {filters.includes("S") ? <StyledCheck /> : null}
                                                        Secondary
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleMenuClick("S");
                                                }}
                                                className={filters.includes("S") ? "active" : null}
                                                style={{ fontSize: "12px" }}
                                            />
                                            <MenuLink
                                                title={
                                                    <>
                                                        {filters.length === 0 ? <StyledCheck /> : null}All
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleMenuClick(null);
                                                }}
                                                className={filters.length === 0 ? "active" : null}
                                                style={{ fontSize: "12px" }}
                                            />
                                        </Menu>
                                    ) : (
                                        <>
                                            <span style={{ marginRight: "5px" }}>Show:</span>
                                            <ReportCheckbox
                                                onChange={() => {
                                                    handleMenuClick("P");
                                                }}
                                                checked={filters.includes("P") ? true : false}
                                            >
                                                Primary
                                            </ReportCheckbox>
                                            <ReportCheckbox
                                                onChange={() => {
                                                    handleMenuClick("S");
                                                }}
                                                checked={filters.includes("S") ? true : false}
                                            >
                                                Secondary
                                            </ReportCheckbox>
                                            <ReportCheckbox
                                                onChange={() => {
                                                    handleMenuClick(null);
                                                }}
                                                checked={filters.length === 0 ? true : false}
                                            >
                                                All
                                            </ReportCheckbox>
                                        </>
                                    )}
                                </SegCell>
                            </SortSegCellWrapper>
                            <SortSegCellWrapper>
                                <SegCell style={{ fontWeight: "bolder" }}>
                                    <PolygonNameWithCircle
                                        circleColor={"lightGreen"}
                                        circleWidth={15}
                                        circleHeight={15}
                                        children={location1Name}
                                    />
                                </SegCell>
                                <SegCell
                                    style={{ flexDirection: "row", alignItems: "flex-end" }}
                                >
                                    <span>Visitor Propensity</span>
                                    <BuxTooltip content={propensityToolTipText} trigger="click" />
                                </SegCell>
                            </SortSegCellWrapper>
                            {location2Name && (
                                <SortSegCellWrapper>
                                    <SegCell style={{ fontWeight: "bolder" }}>
                                        <PolygonNameWithCircle
                                            circleColor={"darkGreen"}
                                            circleWidth={15}
                                            circleHeight={15}
                                            children={location2Name}
                                        />
                                    </SegCell>
                                    <SegCell
                                        style={{ flexDirection: "row", alignItems: "flex-end" }}
                                    >
                                        <span>Visitor Propensity</span>
                                        <BuxTooltip
                                            content={propensityToolTipText}
                                            trigger="click"
                                        />
                                    </SegCell>
                                </SortSegCellWrapper>
                            )}
                        </SortSegRow>
                    </SortSegHead>
                    <SortSegHeadActions
                        className={`${profData?.length >= 5 ? "too-tall" : ""}`}
                    >
                        <SortSegRow>
                            <SortSegCellWrapper>
                                <SegCell>
                                    {location2Name && (
                                        <span>
                                            {windowSize.width < deviceWidth.desktop
                                                ? "Sort By Polygon"
                                                : "Sort Psychographic Profiles by Polygon"}
                                        </span>
                                    )}
                                </SegCell>
                            </SortSegCellWrapper>
                            <SortSegCellWrapperWithActivePolygon
                                className={`${location2Name && sortCol === 1 ? "polygonOneActive" : ""
                                    }`}
                                style={!location2Name ? { borderLeft: "0" } : null}
                            >
                                {location2Name && (
                                    <SegCell>
                                        <Radio
                                            value={1}
                                            onChange={onSortColChange}
                                            defaultChecked={true}
                                            checked={sortCol === 1}
                                        >
                                            {windowSize.width < deviceWidth.desktop
                                                ? "Sort"
                                                : "Sort by"}
                                        </Radio>
                                    </SegCell>
                                )}
                                <SegCell
                                    style={!location2Name ? { justifyContent: "flex-end" } : null}
                                >
                                    {!location2Name && (
                                        <span className="single-case-title">Sort by </span>
                                    )}
                                    <Menu
                                        title={formatSortingText(sort1Option)}
                                        type="filled"
                                        bgColor="black"
                                        color="white"
                                        isOpen={sort1Open}
                                        setIsMenuOpen={setIsSort1MenuOpen}
                                        style={{ margin: 0 }}
                                        buttonStyle={{ minWidth: "131px" }}
                                        fontSize={12}
                                        disabled={sortCol !== 1}
                                        wrapperStyle={{ width: "100%", maxWidth: "220px" }}
                                        iconRight={<ChevronDown size={18} />}
                                    >
                                        <MenuLink
                                            title={
                                                <>
                                                    <AscIcon
                                                        className={
                                                            sort1Option.dir === "asc" ? "active" : null
                                                        }
                                                        size={15}
                                                    />{" "}
                                                    Sort Ascending
                                                </>
                                            }
                                            clickCallback={() => {
                                                handleSortOptionClick(sortCol, {
                                                    dir: "asc",
                                                    type: sort1Option.type,
                                                });
                                            }}
                                            style={{ fontSize: "12px" }}
                                            className={sort1Option.dir === "asc" ? "active" : null}
                                        />
                                        <MenuLink
                                            title={
                                                <>
                                                    <DescIcon
                                                        className={
                                                            sort1Option.dir === "desc" ? "active" : null
                                                        }
                                                        size={15}
                                                    />
                                                    Sort Descending
                                                </>
                                            }
                                            clickCallback={() => {
                                                handleSortOptionClick(sortCol, {
                                                    dir: "desc",
                                                    type: sort1Option.type,
                                                });
                                            }}
                                            style={{ fontSize: "12px" }}
                                            className={sort1Option.dir === "desc" ? "active" : null}
                                        />
                                        <hr />
                                        <MenuLink
                                            title={
                                                <>
                                                    {sort1Option.type === "prop" ? <StyledCheck /> : null}
                                                    Visitor Propensity
                                                </>
                                            }
                                            clickCallback={() => {
                                                handleSortOptionClick(sortCol, {
                                                    dir: sort1Option.dir,
                                                    type: "prop",
                                                });
                                            }}
                                            style={{ fontSize: "12px" }}
                                            className={sort1Option.type === "prop" ? "active" : null}
                                        />
                                        <MenuLink
                                            title={
                                                <>
                                                    {sort1Option.type === "%" ? <StyledCheck /> : null}%
                                                    of Visitors
                                                </>
                                            }
                                            clickCallback={() => {
                                                handleSortOptionClick(sortCol, {
                                                    dir: sort1Option.dir,
                                                    type: "%",
                                                });
                                            }}
                                            style={{ fontSize: "12px" }}
                                            className={sort1Option.type === "%" ? "active" : null}
                                        />
                                        <MenuLink
                                            title={
                                                <>
                                                    {!sort1Option.type ? <StyledCheck /> : null}Segment
                                                    Name
                                                </>
                                            }
                                            clickCallback={() => {
                                                handleSortOptionClick(sortCol, {
                                                    dir: sort1Option.dir,
                                                    type: null,
                                                });
                                            }}
                                            style={{ fontSize: "12px" }}
                                            className={!sort1Option.type ? "active" : null}
                                        />
                                    </Menu>
                                </SegCell>
                            </SortSegCellWrapperWithActivePolygon>
                            {location2Name && (
                                <SortSegCellWrapperWithActivePolygon
                                    className={`${sortCol === 2 ? "polygonTwoActive" : ""}`}
                                >
                                    <SegCell>
                                        <Radio
                                            value={2}
                                            onChange={onSortColChange}
                                            checked={sortCol === 2}
                                        >
                                            {windowSize.width < deviceWidth.desktop
                                                ? "Sort"
                                                : "Sort by"}
                                        </Radio>
                                    </SegCell>
                                    <SegCell>
                                        <Menu
                                            title={formatSortingText(sort2Option)}
                                            type="filled"
                                            bgColor="black"
                                            color="white"
                                            isOpen={sort2Open}
                                            setIsMenuOpen={setIsSort2MenuOpen}
                                            style={{ margin: 0 }}
                                            buttonStyle={{ minWidth: "131px" }}
                                            fontSize={12}
                                            disabled={sortCol !== 2}
                                            wrapperStyle={{ width: "100%", maxWidth: "220px" }}
                                            iconRight={<ChevronDown size={18} />}
                                        >
                                            <MenuLink
                                                title={
                                                    <>
                                                        <AscIcon
                                                            className={
                                                                sort2Option.dir === "asc" ? "active" : null
                                                            }
                                                            size={15}
                                                        />{" "}
                                                        Sort Assending
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleSortOptionClick(sortCol, {
                                                        dir: "asc",
                                                        type: sort2Option.type,
                                                    });
                                                }}
                                                style={{ fontSize: "12px" }}
                                                className={sort2Option.dir === "asc" ? "active" : null}
                                            />
                                            <MenuLink
                                                title={
                                                    <>
                                                        <DescIcon
                                                            className={
                                                                sort2Option.dir === "desc" ? "active" : null
                                                            }
                                                            size={15}
                                                        />
                                                        Sort Descending
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleSortOptionClick(sortCol, {
                                                        dir: "desc",
                                                        type: sort2Option.type,
                                                    });
                                                }}
                                                style={{ fontSize: "12px" }}
                                                className={sort2Option.dir === "desc" ? "active" : null}
                                            />
                                            <hr />
                                            <MenuLink
                                                title={
                                                    <>
                                                        {sort2Option.type === "prop" ? (
                                                            <StyledCheck />
                                                        ) : null}
                                                        Visitor Propensity
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleSortOptionClick(sortCol, {
                                                        dir: sort2Option.dir,
                                                        type: "prop",
                                                    });
                                                }}
                                                style={{ fontSize: "12px" }}
                                                className={
                                                    sort2Option.type === "prop" ? "active" : null
                                                }
                                            />
                                            <MenuLink
                                                title={
                                                    <>
                                                        {sort2Option.type === "%" ? <StyledCheck /> : null}%
                                                        of Visitors
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleSortOptionClick(sortCol, {
                                                        dir: sort2Option.dir,
                                                        type: "%",
                                                    });
                                                }}
                                                style={{ fontSize: "12px" }}
                                                className={sort2Option.type === "%" ? "active" : null}
                                            />
                                            <MenuLink
                                                title={
                                                    <>
                                                        {!sort2Option.type ? <StyledCheck /> : null}
                                                        Segmentation Name
                                                    </>
                                                }
                                                clickCallback={() => {
                                                    handleSortOptionClick(sortCol, {
                                                        dir: sort2Option.dir,
                                                        type: null,
                                                    });
                                                }}
                                                style={{ fontSize: "12px" }}
                                                className={!sort2Option.type ? "active" : null}
                                            />
                                        </Menu>
                                    </SegCell>
                                </SortSegCellWrapperWithActivePolygon>
                            )}
                        </SortSegRow>
                    </SortSegHeadActions>
                    <SortSegBody>
                        {profData && profData.length > 0 ? (
                            profData.map((seg1) => {
                                const segment = mosaicSegments.find(
                                    (seg) => seg1.seg === seg.segmentKey
                                );
                                const seg2 = benchmarkProfileData
                                    ? benchmarkProfileData.find((seg) => seg1.seg === seg.seg)
                                    : null;
                                return (
                                    <SortSegRow key={seg1.seg}>
                                        <SortSegCellWrapper>
                                            <SegCell
                                                onClick={() => {
                                                    setModalContent({
                                                        segment: segment,
                                                        seg1: seg1,
                                                        seg2: seg2,
                                                    });
                                                }}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div>
                                                    <SegImg
                                                        style={{ maxWidth: "85px", maxHeight: "85px" }}
                                                        src={`/Images/segmentation-imgs/${seg1.seg}.jpg`}
                                                    />
                                                </div>
                                                <div>
                                                    {windowSize.width < deviceWidth.desktop ? (
                                                        <>
                                                            <SegCellTextL style={{ marginTop: "6px" }}>
                                                                {seg1.seg}
                                                            </SegCellTextL>
                                                            <SegCellTextSmB
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                {segment.name}{" "}
                                                                <FiExternalLink
                                                                    size={15}
                                                                    style={{ marginLeft: "3px" }}
                                                                />
                                                            </SegCellTextSmB>
                                                        </>
                                                    ) : (
                                                        <SegDetails>
                                                            <SegCellTextL style={{ marginBottom: "10px" }}>
                                                                {seg1.seg}: {segment.name}
                                                            </SegCellTextL>
                                                            <div style={{ color: "#6d7278" }}>
                                                                {segment.description}
                                                            </div>
                                                            <SegLink>
                                                                <FiExternalLink
                                                                    size={15}
                                                                    style={{ marginRight: "7px" }}
                                                                />
                                                                <span>View Details</span>
                                                            </SegLink>
                                                        </SegDetails>
                                                    )}
                                                </div>
                                            </SegCell>
                                        </SortSegCellWrapper>
                                        <SortSegCellWrapper
                                            className={`${location2Name && sortCol === 1 ? "polygonOneActive" : ""
                                                }`}
                                        >
                                            <SegCell>
                                                <PctNumWrapper>
                                                    <SegCellTextL>{calcPctHH(seg1.pct)}%</SegCellTextL>
                                                    <SegCellTextSm>of visitors</SegCellTextSm>
                                                </PctNumWrapper>
                                            </SegCell>
                                            <SegCell>
                                                <SegCirc color={colors[seg1.core]}>
                                                    <SegCircTextL>
                                                        {getPrettyNumber(seg1.index, 2)}X
                                                    </SegCircTextL>
                                                    <SegCircTextSm>
                                                        {determinePropensityText(seg1.core).toUpperCase()}
                                                    </SegCircTextSm>
                                                </SegCirc>
                                            </SegCell>
                                        </SortSegCellWrapper>
                                        {seg2 && (
                                            <SortSegCellWrapper
                                                className={`${sortCol === 2 ? "polygonTwoActive" : ""}`}
                                            >
                                                <SegCell>
                                                    <PctNumWrapper>
                                                        <SegCellTextL>{calcPctHH(seg2.pct)}%</SegCellTextL>
                                                        <SegCellTextSm>of visitors</SegCellTextSm>
                                                    </PctNumWrapper>
                                                </SegCell>
                                                <SegCell>
                                                    <SegCirc color={colors[seg2.core]}>
                                                        <SegCircTextL>
                                                            {getPrettyNumber(seg2.index, 2)}X
                                                        </SegCircTextL>
                                                        <SegCircTextSm>
                                                            {determinePropensityText(seg2.core).toUpperCase()}
                                                        </SegCircTextSm>
                                                    </SegCirc>
                                                </SegCell>
                                            </SortSegCellWrapper>
                                        )}
                                    </SortSegRow>
                                );
                            })
                        ) : (
                            <SortSegRow>
                                <SortSegCellWrapper>
                                    <EmptyResultsCell>
                                        <span>
                                            <GiAchillesHeel size={25} />
                                        </span>
                                        <span style={{ fontSize: "25px" }}>Ouch! No Results!</span>
                                    </EmptyResultsCell>
                                </SortSegCellWrapper>
                            </SortSegRow>
                        )}
                    </SortSegBody>
                    <SortSegFooter
                        className={`${profData?.length >= 5 ? "too-tall" : ""}`}
                    >
                        <SortSegRow>
                            <SortSegCellWrapper></SortSegCellWrapper>
                            <SortSegCellWrapper
                                className={`${location2Name && sortCol === 1 ? "active" : ""}`}
                            ></SortSegCellWrapper>
                            {location2Name && (
                                <SortSegCellWrapper
                                    className={`${sortCol === 2 ? "active" : ""}`}
                                ></SortSegCellWrapper>
                            )}
                        </SortSegRow>
                    </SortSegFooter>
                    <Modal
                        title=""
                        visible={visibleModal}
                        onCancel={() => {
                            setVisibleModal(false);
                        }}
                        footer={null}
                        width="90%"
                        centered={true}
                        bodyStyle={{ padding: "40px 24px 24px" }}
                        style={{ maxWidth: "900px" }}
                    >
                        {modalContent && (
                            <>
                                <SegCardHead>
                                    <div>
                                        <SegImg
                                            src={`/Images/segmentation-imgs/${modalContent.segment.segmentKey}.jpg`}
                                        />
                                    </div>
                                    <div>
                                        <SegCellTextL style={{ marginBottom: "10px" }}>
                                            {modalContent.segment.segmentKey}:{" "}
                                            {modalContent.segment.name}
                                        </SegCellTextL>
                                        <div style={{ fontSize: "15px" }}>
                                            {modalContent.segment.description}
                                        </div>
                                    </div>
                                </SegCardHead>
                                <div>
                                    <SegContentSecHeader>Who We Are</SegContentSecHeader>
                                    <div>
                                        <SegCardHeaderRow>
                                            <SegCardCol></SegCardCol>
                                            <SegCardCol></SegCardCol>
                                            <SegCardSpacer />
                                            <SegCardCol>Index Value</SegCardCol>
                                            <SegCardSpacer />
                                            <SegCardCol>
                                                {modalContent.segment.segmentKey}%
                                            </SegCardCol>
                                        </SegCardHeaderRow>
                                    </div>
                                    <div>
                                        {modalContent.segment.segmentDetails.map((det) => {
                                            return (
                                                <SegCardRow
                                                    key={`${det.segment}-${det.category
                                                        .replace(/\s/g, "-")
                                                        .toLowerCase()}-${det.idx}`}
                                                >
                                                    <SegCardCol>
                                                        {findSegIcon(det.varNm)}
                                                        {det.varNm}:
                                                    </SegCardCol>
                                                    <SegCardCol>{det.varVal}</SegCardCol>
                                                    <SegCardSpacer />
                                                    <SegCardCol>{det.idx}</SegCardCol>
                                                    <SegCardSpacer />
                                                    <SegCardCol>
                                                        {getPrettyNumber(det.pct, 1)}%
                                                    </SegCardCol>
                                                </SegCardRow>
                                            );
                                        })}
                                    </div>
                                </div>
                                <SortSegCardBreakoutContainer>
                                    <SegCardBreakoutItem>
                                        <span>{location1Name}</span>
                                        <br />
                                        {modalContent.segment.segmentKey}s make up{" "}
                                        {calcPctHH(modalContent.seg1.pct)}% of households
                                    </SegCardBreakoutItem>
                                    <SegCardBreakoutItem>
                                        <span>
                                            {getPrettyNumber(modalContent.seg1.index, 2)}X{" "}
                                            {determinePropensityText(modalContent.seg1.core)}
                                        </span>
                                        <br />
                                        Propensity to be a Customer
                                    </SegCardBreakoutItem>
                                    {modalContent.seg2 && location2Name && (
                                        <>
                                            <SegCardBreakoutVerticalBreak />
                                            <SegCardBreakoutItem>
                                                <span>{location2Name}</span>
                                                <br />
                                                {modalContent.segment.segmentKey}s make up{" "}
                                                {calcPctHH(modalContent.seg2.pct)}% of households
                                            </SegCardBreakoutItem>
                                            <SegCardBreakoutItem>
                                                <span>
                                                    {getPrettyNumber(modalContent.seg2.index, 2)}X{" "}
                                                    {determinePropensityText(modalContent.seg2.core)}
                                                </span>
                                                <br />
                                                Propensity to be a Customer
                                            </SegCardBreakoutItem>
                                        </>
                                    )}
                                </SortSegCardBreakoutContainer>
                                <SegFooterSubText>
                                    *Index score of the characteristics representation in{" "}
                                    {modalContent.segment.segmentKey}s compared to the US
                                    population. Above 100 means the variable is over-represented
                                    compared to the US population. Numbers on the right show the
                                    percentage of {modalContent.segment.segmentKey}s that exhibit
                                    this characteristic.
                                </SegFooterSubText>
                            </>
                        )}
                    </Modal>
                </>
            </>
        );
    };
